import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowRight, X, Sparkle, UploadSimple, CheckCircle } from "@phosphor-icons/react";

const STEPS = ["welcome", "cv-import", "profile", "done"];

export default function OnboardingWizard({ onClose }) {
  const [step, setStep] = useState("welcome");
  const [file, setFile] = useState(null);
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [transcriptLevel, setTranscriptLevel] = useState("Module");
  const [transcriptBusy, setTranscriptBusy] = useState(false);
  const [transcriptResult, setTranscriptResult] = useState(null);
  const [parseBusy, setParseBusy] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [applyBusy, setApplyBusy] = useState(false);
  const [profile, setProfile] = useState({ headline: "", location: "", linkedin: "" });
  const nav = useNavigate();

  const skipAll = async () => {
    await markDone();
    onClose?.();
  };

  const markDone = async () => {
    try {
      localStorage.setItem("vault_onboarded", "1");
    } catch (e) {
      // Private-mode browsers may block localStorage — silently okay, user just sees onboarding again.
      console.warn("localStorage unavailable:", e?.message || e);
    }
  };

  const parseCV = async () => {
    // No CV, but they picked a transcript → skip CV parse but still parse transcript
    if (!file && transcriptFile) {
      await parseTranscript();
      return;
    }
    if (!file) return setStep("profile");
    setParseBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/cv", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setSuggestions(data);
      // If the user also picked a transcript, parse it in the same step so
      // both previews show together before "add it all"
      if (transcriptFile) await parseTranscript();
    } catch (e) {
      toast.error(e.response?.data?.detail || "couldn't parse — moving on");
      setStep("profile");
    } finally { setParseBusy(false); }
  };

  const parseTranscript = async () => {
    if (!transcriptFile) return;
    setTranscriptBusy(true);
    try {
      const fd = new FormData(); fd.append("file", transcriptFile);
      const { data } = await api.post("/ai/extract-subjects/document", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { default_level: transcriptLevel || "Module" },
        timeout: 120000,
      });
      setTranscriptResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "couldn't parse transcript — carrying on");
    } finally { setTranscriptBusy(false); }
  };

  const applyAll = async () => {
    setApplyBusy(true);
    try {
      // Merge any parsed transcript into a fresh education entry
      const transcriptEducation = transcriptResult ? [{
        qualification: "",
        provider: "",
        subjects: (transcriptResult.subjects || []).map((s) => ({
          subject: s.subject || "",
          grade:   s.grade   || "",
          year:    s.year    || "",
          level:   s.level   || transcriptLevel || "Module",
          module_code: s.module_code || "",
          mark:        s.mark || "",
          result:      s.result || "",
          credits:     s.credits || "",
          attempt:     s.attempt || "",
        })),
        classification:   transcriptResult.qualification?.classification    || "",
        final_mark:       transcriptResult.qualification?.final_mark        || "",
        credits_achieved: transcriptResult.qualification?.credits_achieved  || "",
        mode_of_study:    transcriptResult.qualification?.mode_of_study     || "",
      }] : [];
      const payload = {
        personal_info: suggestions?.personal_info,
        work_experience: suggestions?.work_experience || [],
        education: [...(suggestions?.education || []), ...transcriptEducation],
        apprenticeships: suggestions?.apprenticeships || [],
        skills: suggestions?.skills || [],
        references: suggestions?.references || [],
        certifications: suggestions?.certifications || [],
        languages: suggestions?.languages || [],
        awards: suggestions?.awards || [],
        publications: suggestions?.publications || [],
        projects: suggestions?.projects || [],
        volunteer_work: suggestions?.volunteer_work || [],
      };
      const { data } = await api.post("/import/cv/apply", payload);
      const transcriptNote = transcriptResult ? ` · +1 transcript entry (${(transcriptResult.subjects || []).length} rows)` : "";
      const stashNote = (suggestions?.saved_document_id || transcriptResult?.saved_document_id) ? " · file(s) saved to Document Safe" : "";
      const extraBits = [
        data.skills && `${data.skills} skills`,
        data.references && `${data.references} refs`,
        data.certifications && `${data.certifications} certs`,
        data.languages && `${data.languages} langs`,
        data.awards && `${data.awards} awards`,
        data.publications && `${data.publications} pubs`,
        data.projects && `${data.projects} projects`,
        data.volunteer_work && `${data.volunteer_work} volunteer`,
      ].filter(Boolean).join(" · ");
      const extras = extraBits ? ` · ${extraBits}` : "";
      toast.success(`added ${data.work} work · ${data.education} edu · ${data.apprenticeships} appr${extras}${transcriptNote}${stashNote}`);
      setStep("profile");
    } catch {
      toast.error("couldn't apply");
    } finally { setApplyBusy(false); }
  };

  const saveProfile = async () => {
    try {
      const { data: current } = await api.get("/profile");
      const merged = { ...(current?.personal_info || {}), ...profile };
      // only set non-empty fields
      Object.keys(merged).forEach((k) => { if (merged[k] === undefined) delete merged[k]; });
      await api.put("/profile", merged);
      setStep("done");
    } catch {
      toast.error("couldn't save");
    }
  };

  const finish = async () => {
    await markDone();
    onClose?.();
    nav("/dashboard");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-wizard">
      <div className="bg-[var(--brand-bg)] card-hard rounded-md w-full max-w-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b-[1.5px] border-[var(--brand-ink)] bg-[var(--pastel-yellow)]">
          <p className="label-tiny">/onboarding · step {STEPS.indexOf(step) + 1} of {STEPS.length}</p>
          <button onClick={skipAll} data-testid="onboarding-skip" className="text-sm underline">skip all</button>
        </div>

        <div className="p-8">
          {step === "welcome" && (
            <div>
              <h2 className="font-display text-4xl font-semibold tracking-tight">welcome to vault.</h2>
              <p className="text-[var(--brand-muted)] mt-3 max-w-md">
                three quick steps. then you never type your address into a job form again.
              </p>
              <ol className="mt-6 space-y-2 text-sm">
                <li className="flex gap-2"><span className="font-mono-d text-[var(--brand-muted)]">01.</span> drop a CV → we extract your work, edu, and skills <span className="text-[var(--brand-muted)]">(add a transcript too if you&apos;ve got one)</span></li>
                <li className="flex gap-2"><span className="font-mono-d text-[var(--brand-muted)]">02.</span> add a quick headline + linkedin</li>
                <li className="flex gap-2"><span className="font-mono-d text-[var(--brand-muted)]">03.</span> grab the browser extension. apply faster.</li>
              </ol>
              <button onClick={() => setStep("cv-import")} data-testid="onboarding-next-welcome"
                className="mt-8 btn-hard bg-[var(--brand-primary)] text-white rounded-md px-5 py-3 flex items-center gap-2 hover:bg-[var(--brand-primary)]">
                let&apos;s go <ArrowRight size={16} weight="bold"/>
              </button>
            </div>
          )}

          {step === "cv-import" && !suggestions && !transcriptResult && (
            <div>
              <p className="label-tiny mb-2">/step 1</p>
              <h2 className="font-display text-3xl font-semibold tracking-tight">drop your cv.</h2>
              <p className="text-[var(--brand-muted)] mt-2">we&apos;ll parse it with AI. you can review before anything is saved.</p>
              <input type="file" accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(e) => setFile(e.target.files[0])}
                data-testid="onboarding-cv-file"
                className="mt-6 card-hard rounded-md px-3 py-2 bg-white text-sm w-full"/>

              {/* Secondary — transcript upload. Deliberately styled quieter than the
                  CV picker; not everyone has a formal transcript to hand. */}
              <details className="mt-5 card-hard rounded-md p-4 bg-[var(--pastel-mint)]/60">
                <summary className="cursor-pointer text-sm font-medium flex items-center gap-2">
                  <Sparkle size={14} weight="fill"/> also got an academic transcript or GCSE certificate?
                  <span className="font-mono-d text-[10px] text-[var(--brand-muted)]">(optional)</span>
                </summary>
                <p className="text-xs text-[var(--brand-muted)] mt-2 mb-3">
                  drop it in and we&apos;ll turn every module / subject row into structured education data. skip this if you don&apos;t have one handy — you can always add it later.
                </p>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <label className="text-xs text-[var(--brand-muted)] inline-flex items-center gap-1.5">
                    default level
                    <select
                      data-testid="onboarding-transcript-level"
                      value={transcriptLevel}
                      onChange={(e) => setTranscriptLevel(e.target.value)}
                      className="card-hard rounded-md px-2 py-1 bg-white text-xs">
                      <option value="GCSE">GCSE</option>
                      <option value="A-Level">A-Level</option>
                      <option value="BTEC">BTEC</option>
                      <option value="T-Level">T-Level</option>
                      <option value="Module">Module (uni)</option>
                      <option value="IB">IB</option>
                    </select>
                  </label>
                </div>
                <input type="file"
                  accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp"
                  onChange={(e) => setTranscriptFile(e.target.files[0])}
                  data-testid="onboarding-transcript-file"
                  className="card-hard rounded-md px-3 py-2 bg-white text-sm w-full"/>
                {transcriptFile && (
                  <p className="text-xs text-[var(--brand-muted)] mt-2 font-mono-d">
                    picked: {transcriptFile.name} — we&apos;ll parse it alongside your CV.
                  </p>
                )}
              </details>

              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={parseCV} disabled={parseBusy || transcriptBusy || (!file && !transcriptFile)} data-testid="onboarding-cv-parse"
                  className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 flex items-center gap-2 disabled:opacity-60 hover:bg-[var(--brand-primary)]">
                  <Sparkle size={16} weight="duotone"/> {parseBusy || transcriptBusy ? "parsing…" : (transcriptFile && !file ? "parse transcript" : "parse cv")}
                </button>
                <button onClick={() => setStep("profile")} className="btn-hard bg-white rounded-md px-4 py-2.5">skip this step</button>
              </div>
            </div>
          )}

          {step === "cv-import" && (suggestions || transcriptResult) && (
            <div data-testid="onboarding-cv-review">
              <p className="label-tiny mb-2">/step 1 · review</p>
              <h2 className="font-display text-3xl font-semibold tracking-tight">looks good?</h2>
              <p className="text-[var(--brand-muted)] mt-2 mb-5">we&apos;ll add all of these. you can edit later.</p>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {suggestions?.personal_info?.full_name && (
                  <div className="text-sm flex items-center gap-2"><CheckCircle size={16} weight="fill"/> {suggestions.personal_info.full_name}</div>
                )}
                {(suggestions?.work_experience || []).map((w, i) => (
                  <div key={`w${i}`} className="text-sm flex items-start gap-2"><CheckCircle size={16} weight="fill" className="mt-0.5"/> <span><b>work:</b> {w.role} · {w.employer}</span></div>
                ))}
                {(suggestions?.education || []).map((e, i) => (
                  <div key={`e${i}`} className="text-sm flex items-start gap-2"><CheckCircle size={16} weight="fill" className="mt-0.5"/> <span><b>edu:</b> {e.qualification} · {e.provider}</span></div>
                ))}
                {(suggestions?.apprenticeships || []).map((a, i) => (
                  <div key={`a${i}`} className="text-sm flex items-start gap-2"><CheckCircle size={16} weight="fill" className="mt-0.5"/> <span><b>appr:</b> {a.role} @ {a.employer}</span></div>
                ))}
                {transcriptResult && (
                  <div className="text-sm flex items-start gap-2" data-testid="onboarding-transcript-preview">
                    <CheckCircle size={16} weight="fill" className="mt-0.5 text-[var(--brand-primary)]"/>
                    <span><b>transcript:</b> {(transcriptResult.subjects || []).length} row{(transcriptResult.subjects || []).length === 1 ? "" : "s"}
                      {transcriptResult.qualification?.classification ? ` · classification: ${transcriptResult.qualification.classification}` : ""}
                      {transcriptResult.qualification?.final_mark ? ` · final mark: ${transcriptResult.qualification.final_mark}` : ""}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={applyAll} disabled={applyBusy} data-testid="onboarding-cv-apply"
                  className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 flex items-center gap-2 disabled:opacity-60 hover:bg-[var(--brand-primary)]">
                  {applyBusy ? "adding…" : <>add it all <ArrowRight size={16} weight="bold"/></>}
                </button>
                <button onClick={() => { setSuggestions(null); setFile(null); setTranscriptResult(null); setTranscriptFile(null); }} className="btn-hard bg-white rounded-md px-4 py-2.5">re-upload</button>
              </div>
            </div>
          )}

          {step === "profile" && (
            <div data-testid="onboarding-profile">
              <p className="label-tiny mb-2">/step 2</p>
              <h2 className="font-display text-3xl font-semibold tracking-tight">finish your profile.</h2>
              <p className="text-[var(--brand-muted)] mt-2 mb-5">just the high-impact bits. you can fill in more later.</p>
              <div className="space-y-3">
                <div>
                  <label className="label-tiny block mb-1.5">one-line headline</label>
                  <input data-testid="onboarding-headline" value={profile.headline}
                    onChange={(e) => setProfile({ ...profile, headline: e.target.value })}
                    placeholder="e.g. Junior Designer · Remote-friendly"
                    className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
                </div>
                <div>
                  <label className="label-tiny block mb-1.5">location</label>
                  <input data-testid="onboarding-location" value={profile.location}
                    onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                    placeholder="London, UK"
                    className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
                </div>
                <div>
                  <label className="label-tiny block mb-1.5">linkedin url</label>
                  <input data-testid="onboarding-linkedin" value={profile.linkedin}
                    onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/in/…"
                    className="w-full card-hard rounded-md px-3 py-2 bg-white"/>
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={saveProfile} data-testid="onboarding-save-profile"
                  className="btn-hard bg-[var(--brand-primary)] text-white rounded-md px-4 py-2.5 flex items-center gap-2 hover:bg-[var(--brand-primary)]">
                  save & continue <ArrowRight size={16} weight="bold"/>
                </button>
                <button onClick={() => setStep("done")} className="btn-hard bg-white rounded-md px-4 py-2.5">skip</button>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="text-center" data-testid="onboarding-done">
              <CheckCircle size={48} weight="fill" className="mx-auto text-[var(--brand-primary)]"/>
              <h2 className="font-display text-3xl font-semibold tracking-tight mt-4">you&apos;re vaulted.</h2>
              <p className="text-[var(--brand-muted)] mt-2 max-w-md mx-auto">
                next: grab the chrome extension from the profile page. then go apply for things.
              </p>
              <button onClick={finish} data-testid="onboarding-finish"
                className="mt-8 btn-hard bg-[var(--brand-ink)] text-white rounded-md px-5 py-3 flex items-center gap-2 mx-auto hover:bg-[var(--brand-ink)]">
                open dashboard <ArrowRight size={16} weight="bold"/>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
